import React, { useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { api } from '@/lib/api';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Users, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Full-screen viewer modal — subscribes to a LiveKit room and renders the
 * broadcaster's video + audio. Uses subscribe-only token minted server-side.
 *
 * Props:
 *  - open       : bool
 *  - room       : LiveKit room name (from /golive/active)
 *  - hostName   : display name to show in the pill
 *  - venueName  : venue name to show next to host
 *  - onClose    : () => void
 */
export function LiveViewerDialog({ open, room, hostName, venueName, onClose }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const roomRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [viewers, setViewers] = useState(0);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (!open || !room) return undefined;
    let cancelled = false;

    (async () => {
      setStatus('connecting');
      try {
        const { data } = await api.post('/live/viewer-token', { room });
        const rm = new Room({ adaptiveStream: true, dynacast: true });
        roomRef.current = rm;

        rm.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === Track.Kind.Video && videoRef.current) {
            track.attach(videoRef.current);
          }
          if (track.kind === Track.Kind.Audio && audioRef.current) {
            track.attach(audioRef.current);
          }
        });
        const syncViewers = () => setViewers(rm.remoteParticipants.size);
        rm.on(RoomEvent.ParticipantConnected, syncViewers);
        rm.on(RoomEvent.ParticipantDisconnected, syncViewers);
        rm.on(RoomEvent.Disconnected, () => setStatus('ended'));

        await rm.connect(data.url, data.token);
        if (cancelled) { await rm.disconnect(); return; }
        syncViewers();
        setStatus('watching');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[LiveKit] viewer failed', e);
        toast.error(e?.response?.data?.detail || 'Cannot join stream');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      try { roomRef.current?.disconnect(); } catch (e) { /* noop */ }
      roomRef.current = null;
    };
  }, [open, room]);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !audioRef.current.muted;
    setMuted(audioRef.current.muted);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-md p-0 border-white/10 bg-[#07070A] text-white overflow-hidden"
        data-testid="live-viewer-dialog"
      >
        <div className="relative">
          <video
            ref={videoRef}
            playsInline autoPlay muted={muted}
            className="w-full aspect-[9/16] bg-black object-cover"
            data-testid="live-viewer-video"
          />
          {/* Hidden audio sink so we can control mute independently of the video element */}
          <audio ref={audioRef} autoPlay />

          <div className="absolute top-3 left-3 flex items-center gap-2 max-w-[70%]">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
              <span className="inline-flex h-2 w-2 rounded-full bg-white th-live-dot" /> Live
            </span>
            {hostName && (
              <span className="rounded-full bg-black/60 px-2.5 py-1 text-[11px] truncate">
                {hostName}{venueName ? ` · ${venueName}` : ''}
              </span>
            )}
          </div>
          <div
            className="absolute top-3 right-3 rounded-full bg-black/60 px-2.5 py-1 text-xs inline-flex items-center gap-1"
            data-testid="live-viewer-count"
          >
            <Users className="h-3.5 w-3.5" /> {viewers}
          </div>

          {status !== 'watching' && (
            <div
              className="absolute inset-0 grid place-items-center bg-black/70 text-sm text-white/85"
              data-testid="live-viewer-status"
            >
              {status === 'connecting' && 'Connecting to the stream…'}
              {status === 'ended' && 'This stream has ended'}
              {status === 'error' && 'Could not connect to the stream'}
              {status === 'idle' && ' '}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-3 border-t border-white/10 bg-[#0B0C10]">
          <button
            type="button" onClick={toggleAudio}
            className="rounded-full border border-white/12 bg-white/5 hover:bg-white/10 px-3 py-2 text-xs inline-flex items-center gap-2"
            data-testid="live-viewer-audio-toggle"
          >
            {muted
              ? <><VolumeX className="h-4 w-4" /> Tap for sound</>
              : <><Volume2 className="h-4 w-4" /> Sound on</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default LiveViewerDialog;
